import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import type { AdminLeague, LeagueDisableImpact } from '@/types/league';
import { hasDisableImpact } from '@/utils/leagueDisableImpact';

/**
 * Admin "Enabled" toggle for a league. Enabling applies at once. Disabling
 * first loads the league's disable impact; when tournaments or unfinished
 * matches are affected the impact is exposed as `pendingDisable` and the
 * toggle waits for `confirmDisable` / `cancelDisable`, otherwise it applies.
 */
export function useLeagueEnabledToggle(onError: (text: string) => void) {
  const { t } = useTranslation('admin');
  const queryClient = useQueryClient();
  const [togglingLeagueId, setTogglingLeagueId] = useState<number | null>(null);
  const [pendingDisable, setPendingDisable] = useState<LeagueDisableImpact | null>(null);

  const toggleMutation = useMutation({
    mutationFn: async ({ leagueId, isEnabled }: { leagueId: number; isEnabled: boolean }) => {
      setTogglingLeagueId(leagueId);
      await apiClient.toggleLeagueEnabled(leagueId, isEnabled);
      return { leagueId, isEnabled };
    },
    onSuccess: ({ leagueId, isEnabled }) => {
      queryClient.setQueriesData<AdminLeague[]>({ queryKey: ['admin-leagues'] }, (prev) =>
        prev?.map((l) => (l.id === leagueId ? { ...l, isEnabled } : l)),
      );
    },
    onError: (error: any) => {
      onError(error.response?.data?.error || t('admin:toggleLeagueError'));
    },
    onSettled: () => setTogglingLeagueId(null),
  });

  const requestToggle = useCallback(
    async (league: AdminLeague) => {
      if (!league.isEnabled) {
        toggleMutation.mutate({ leagueId: league.id, isEnabled: true });
        return;
      }
      setTogglingLeagueId(league.id);
      try {
        const impact = (await apiClient.getLeagueDisableImpact(league.id)).data.data;
        if (hasDisableImpact(impact)) {
          setTogglingLeagueId(null);
          setPendingDisable(impact);
          return;
        }
      } catch (error: any) {
        setTogglingLeagueId(null);
        onError(error.response?.data?.error || t('admin:leagueDisableImpactError'));
        return;
      }
      toggleMutation.mutate({ leagueId: league.id, isEnabled: false });
    },
    [toggleMutation, onError, t],
  );

  const confirmDisable = useCallback(() => {
    if (!pendingDisable) return;
    toggleMutation.mutate({ leagueId: pendingDisable.leagueId, isEnabled: false });
    setPendingDisable(null);
  }, [pendingDisable, toggleMutation]);

  const cancelDisable = useCallback(() => setPendingDisable(null), []);

  return { togglingLeagueId, requestToggle, pendingDisable, confirmDisable, cancelDisable };
}
